/**
 * Quick actions rendered at the top of the collapsed editor rail (#2230).
 *
 * The rail spec calls out "logo, new, home, search, library, then the
 * user avatar" — so when the sidebar is collapsed we render only these
 * four action icons (New, Home, Search, Library) between the logo and
 * the user footer. The full main nav returns when the sidebar is
 * expanded; the rail stays minimal on purpose.
 *
 * Path choices:
 * - Home → `PATHS().HOME` (workspace dashboard).
 * - Library → `PATHS().STT` (speech-to-text sessions list — the
 *   long-lived record of what the user has processed, which is the
 *   "library" the reviewer refers to in #2246-adjacent AC lines).
 * - New → dropdown that opens the same `Upload` modal the expanded
 *   sidebar exposes inline.
 * - Search → opens `SearchOverlay`, a modal wrapper around
 *   `GlobalSearch` so the editor can access the same search UX as the
 *   main top bar (which isn't rendered on editor pages).
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { FileText, Home, Library, Plus, Search, Subtitles } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import Upload from '@components/shared/upload/upload.tsx';
import GlobalSearch from '@components/layout/navbar/global-search.tsx';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { PATHS } from '../../../router/paths.ts';

/**
 * Icon-only rail action. Kept in this file (not `NavItemRow`) so
 * button-style actions (with `onClick`) sit alongside link-style ones
 * without forcing `NavItemRow` to grow a discriminated union.
 */
function RailIconButton({
  label,
  icon: Icon,
  onClick,
  active,
  ariaExpanded
}: {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  active?: boolean;
  ariaExpanded?: boolean;
}) {
  const { getColour, getBrandTint } = useThemeContext();
  return (
    <Tooltip label={label} placement="right">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-expanded={ariaExpanded}
        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        style={{
          color: active ? getColour('activeText') : getColour('normalText'),
          backgroundColor: active ? getBrandTint(0.12) : 'transparent'
        }}
      >
        <Icon className="h-5 w-5 shrink-0" />
      </button>
    </Tooltip>
  );
}

/**
 * Icon-only rail link. Uses `NavLink` so the active route lights the
 * button up automatically — same visual language as `NavItemRow`.
 */
function RailIconLink({
  to,
  label,
  icon: Icon
}: {
  to: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
  const { getColour, getBrandTint } = useThemeContext();
  return (
    <Tooltip label={label} placement="right">
      <NavLink
        to={to}
        end
        aria-label={label}
        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        style={({ isActive }) => ({
          color: isActive ? getColour('activeText') : getColour('normalText'),
          backgroundColor: isActive ? getBrandTint(0.12) : 'transparent'
        })}
      >
        <Icon className="h-5 w-5 shrink-0" />
      </NavLink>
    </Tooltip>
  );
}

function RailNewAction() {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const { hasFeature } = useEntitlements();
  const [type, setType] = useState<DocumentTypeOptions | 'align' | ''>('');
  const uploadDisabled = !hasFeature('batch') && !hasFeature('translation');

  return (
    <>
      <Menu>
        {({ open }) => (
          <>
            <Tooltip label={t('new')} placement="right">
              <MenuButton
                aria-label={t('new')}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                style={{
                  color: open
                    ? getColour('activeText')
                    : getColour('normalText'),
                  backgroundColor: open ? getBrandTint(0.12) : 'transparent'
                }}
              >
                <Plus className="h-5 w-5 shrink-0" />
              </MenuButton>
            </Tooltip>
            {/* `anchor` floats the panel with Headless UI's built-in
                portal so it escapes the sidebar's `overflow-y-auto`
                scroller — otherwise the dropdown opens inside the
                clipped scroll container and reads as "did nothing". */}
            <MenuItems
              anchor="right start"
              className="z-50 w-56 rounded-2xl py-1 focus:outline-none [--anchor-gap:8px]"
              style={{
                backgroundColor: getColour('thirdLayer'),
                boxShadow:
                  '0 4px 24px -4px rgba(0,0,0,0.18), 0 0 0 1px ' +
                  getColour('hairline')
              }}
            >
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => setType('document')}
                    disabled={uploadDisabled}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-left',
                      'disabled:opacity-40 disabled:cursor-not-allowed'
                    )}
                    style={{
                      color: getColour('normalText'),
                      backgroundColor: focus
                        ? getBrandTint(0.06)
                        : 'transparent'
                    }}
                  >
                    <FileText size={16} className="shrink-0" />
                    {t('new-transcript')}
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => setType('caption')}
                    disabled={uploadDisabled}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-left',
                      'disabled:opacity-40 disabled:cursor-not-allowed'
                    )}
                    style={{
                      color: getColour('normalText'),
                      backgroundColor: focus
                        ? getBrandTint(0.06)
                        : 'transparent'
                    }}
                  >
                    <Subtitles size={16} className="shrink-0" />
                    {t('subtitles')}
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </>
        )}
      </Menu>
      {/* `Upload` opens itself when `type !== ''` and calls `setType('')`
          on close. Keeping the mount here (not conditionally) so the
          modal can animate closed even after the menu itself unmounts. */}
      <Upload type={type} setType={setType} />
    </>
  );
}

/**
 * Reuses the exact same `GlobalSearch` component the main top bar
 * mounts — just floats it as a command-palette-style overlay so it can
 * work inside the editor where the top bar is suppressed. Wrapping it
 * in `Modal` clipped `GlobalSearch`'s absolute-positioned dropdown, so
 * we render our own light overlay and give the panel room to grow.
 */
function RailSearchAction() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on route change — `GlobalSearch` navigates on select and
  // clears its own dropdown, but the overlay has to follow or it
  // lingers over the newly rendered page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape closes; click outside the panel closes. Attached only while
  // open so we don't leak listeners.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  return (
    <>
      <RailIconButton
        label={t('search')}
        icon={Search}
        onClick={() => setOpen(true)}
        ariaExpanded={open}
      />
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('search')}
            className="fixed inset-0 z-[1000] flex items-start justify-center p-4 pt-24"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div
              ref={panelRef}
              className="w-full max-w-2xl rounded-2xl p-3 shadow-pop"
              style={{
                backgroundColor: getColour('secondLayer'),
                border: `1px solid ${getColour('hairline')}`
              }}
            >
              {/* GlobalSearch renders its own dropdown as `absolute`
                  below its input; keeping the panel container tall
                  and un-clipped lets the results grow naturally. */}
              <GlobalSearch />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * The four quick actions rendered at the top of the collapsed rail.
 * Order matches the ticket spec: New, Home, Search, Library.
 */
export function RailQuickActions() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-1">
      <RailNewAction />
      <RailIconLink to={PATHS().HOME} label={t('home')} icon={Home} />
      <RailSearchAction />
      <RailIconLink to={PATHS().STT} label={t('library')} icon={Library} />
    </div>
  );
}
